import React, { useState } from "react";
import {
  Modal,
  TextInput,
  Textarea,
  Button,
  Stack,
  LoadingOverlay,
  SimpleGrid,
  ActionIcon,
  Text,
  Image,
  Group,
  Divider,
  Box,
} from "@mantine/core";

import { IconTrash, IconUpload } from "@tabler/icons-react";

import { useNotification } from "../../adminProjectNotifications/popupnotifications";
import { API_BASE_URL } from "../../../config/baseUrls";

const BASE_URL = `${API_BASE_URL}/shop-owner`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("shopToken")}`,
  "Content-Type": "application/json",
});

const ImageUploadComponent = ({ onUpload, existingUrl, className }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(existingUrl);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = async () => {
      const base64 = reader.result.split(",")[1];
      try {
        const res = await fetch(
          `${API_BASE_URL}/auth/uploadImage`,
          {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify({ image: base64, name: file.name }),
          },
        );
        const data = await res.json();
        if (data.url) {
          setPreview(data.url);
          onUpload(data.url);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setUploading(false);
      }
    };
  };

  return (
    <Box className={className}>
      {preview ? (
        <Box
          pos="relative"
          w="100%"
          h={160}
          bg="gray.1"
          style={{ borderRadius: 8, overflow: "hidden" }}
        >
          <Image src={preview} h="100%" w="100%" fit="cover" />
          <ActionIcon
            color="red"
            variant="filled"
            pos="absolute"
            bottom={10}
            right={10}
            onClick={() => {
              setPreview(null);
              onUpload("");
            }}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Box>
      ) : (
        <Button
          component="label"
          variant="outline"
          color="gray"
          fullWidth
          h={120}
          style={{ borderStyle: "dashed" }}
          loading={uploading}
        >
          <Stack gap={0} align="center">
            <IconUpload size={24} />
            <Text size="sm" mt={4}>
              Upload Image
            </Text>
          </Stack>
          <input type="file" hidden accept="image/*" onChange={handleFile} />
        </Button>
      )}
    </Box>
  );
};

export { ImageUploadComponent };

export default function CreateShopModal({ isOpen, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    bannerImageUrl: "",
    logoUrl: "",
    socialLinks: { instagram: "", facebook: "", twitter: "", website: "" },
  });
  const { showSuccess, showError } = useNotification();

  const handleSubmit = async () => {
    if (!formData.name)
      return showError("Missing Data", "Shop Name is required");
    setLoading(true);

    try {
      const res = await fetch(`${BASE_URL}/shops`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(formData),
      });
      const data = await res.json();

      if (data.success) {
        showSuccess("Created", "Shopfront created successfully");
        if (onSuccess) onSuccess(); // Refresh the list
        onClose();
        // Reset Form
        setFormData({
          name: "",
          description: "",
          bannerImageUrl: "",
          logoUrl: "",
          socialLinks: {
            instagram: "",
            facebook: "",
            twitter: "",
            website: "",
          },
        });
      } else {
        showError("Error", data.error || "Operation failed");
      }
    } catch (e) {
      showError("Error", "Network error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={
        <Text fw={700} size="lg">
          Create New Shopfront
        </Text>
      }
      size="lg"
      padding="xl"
    >
      <Stack gap="md" pos="relative">
        <LoadingOverlay visible={loading} />

        <TextInput
          label="Shop Name"
          placeholder="e.g. Anime Merch Store"
          required
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />

        <Textarea
          label="Description"
          placeholder="Brief about the shop..."
          minRows={3}
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
        />

        <Divider label="Social Media" labelPosition="center" my="xs" />

        <SimpleGrid cols={2}>
          <TextInput
            label="Instagram URL"
            placeholder="@username"
            value={formData.socialLinks.instagram}
            onChange={(e) =>
              setFormData({
                ...formData,
                socialLinks: {
                  ...formData.socialLinks,
                  instagram: e.target.value,
                },
              })
            }
          />
          <TextInput
            label="Facebook URL"
            placeholder="https://facebook.com/..."
            value={formData.socialLinks.facebook}
            onChange={(e) =>
              setFormData({
                ...formData,
                socialLinks: {
                  ...formData.socialLinks,
                  facebook: e.target.value,
                },
              })
            }
          />
          <TextInput
            label="Twitter / X URL"
            placeholder="https://twitter.com/..."
            value={formData.socialLinks.twitter}
            onChange={(e) =>
              setFormData({
                ...formData,
                socialLinks: {
                  ...formData.socialLinks,
                  twitter: e.target.value,
                },
              })
            }
          />
          <TextInput
            label="Website URL"
            placeholder="https://myshop.com"
            value={formData.socialLinks.website}
            onChange={(e) =>
              setFormData({
                ...formData,
                socialLinks: {
                  ...formData.socialLinks,
                  website: e.target.value,
                },
              })
            }
          />
        </SimpleGrid>

        <Divider label="Branding" labelPosition="center" my="xs" />

        <SimpleGrid cols={2}>
          <Box>
            <Text size="sm" fw={500} mb={4}>
              Logo (Square)
            </Text>
            <Box h={120}>
              <ImageUploadComponent
                onUpload={(url) => setFormData({ ...formData, logoUrl: url })}
                existingUrl={formData.logoUrl}
              />
            </Box>
          </Box>
          <Box>
            <Text size="sm" fw={500} mb={4}>
              Banner (Wide)
            </Text>
            <Box h={120}>
              <ImageUploadComponent
                onUpload={(url) =>
                  setFormData({ ...formData, bannerImageUrl: url })
                }
                existingUrl={formData.bannerImageUrl}
              />
            </Box>
          </Box>
        </SimpleGrid>

        <Group justify="flex-end" mt="lg">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="blue"
            className="btn-ota-primary"
          >
            Create Shop
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
